import { getStargateBaseUrl } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';

import { logFastshiftTrace } from './fastshift-trace-dev-log';
import { getFastshiftCustomTracingData } from './get-fastshift-custom-tracing-data';
import { getFastshiftRouteKey } from './get-fastshift-route-key';

const SAMPLED_HEADER = 'x-b3-sampled';
const MAX_SAMPLE_RATE = 100;

let isInstalled = false;

const getRequestUrl = (input: RequestInfo | URL): string => {
	if (typeof input === 'string') {
		return input;
	}

	return 'url' in input ? input.url : input.href;
};

const getStargateOrigin = (): string | undefined => {
	try {
		// Resolved against the page so an empty or relative base URL collapses to the page origin
		// rather than throwing.
		return new URL(getStargateBaseUrl() ?? '', window.location.href).origin;
	} catch {
		return undefined;
	}
};

/**
 * Restricted to origins we own. Adding a header to a cross-origin request forces a CORS
 * preflight, and the CSP allows a long tail of third parties (Sentry, OneTrust, GTM, reCAPTCHA,
 * the chat widget) that must not be touched. Stargate is normally same-origin, but is checked
 * separately because the `*.frontend.public.atl-paas.net` hostnames serve the app from a
 * different origin than the gateway.
 */
const isTraceableOrigin = (url: string): boolean => {
	try {
		const { origin } = new URL(url, window.location.href);

		return origin === window.location.origin || origin === getStargateOrigin();
	} catch {
		return false;
	}
};

/**
 * Narrows sampling to calls for FastShift data, so the traces we force are the ones worth keeping.
 * Everything else the page fetches from our own origins — post office placements, analytics,
 * assets — is left alone regardless of the configured route scope.
 *
 * Every FastShift backend endpoint is namespaced under a `fastshift-portal` path segment, appended
 * to whichever base URL `getStargateBaseUrl` returns: `/gateway/api/fastshift-portal/…` on the
 * FastShift domains, or `/fastshift-portal/…` if it falls back to the WAC base URL. Matching the
 * segment rather than either full prefix covers both, and the surrounding slashes keep an asset
 * such as `/assets/fastshift-portal.js` from matching.
 */
const FASTSHIFT_API_PATH_SEGMENT = '/fastshift-portal/';

const isFastshiftApiRequest = (url: string): boolean => {
	try {
		return new URL(url, window.location.href).pathname.includes(FASTSHIFT_API_PATH_SEGMENT);
	} catch {
		return false;
	}
};

const shouldSampleRequest = (url: string): boolean => {
	const { tracingEnabled, sampleRate, allRoutes, specificRoutes } = getFastshiftCustomTracingData();

	if (!tracingEnabled) {
		logFastshiftTrace(`skip ${url} — tracing is not enabled in the dynamic config`);

		return false;
	}

	if (allRoutes) {
		logFastshiftTrace(`${url} — config applies to all routes`);
	} else {
		const routeKey = getFastshiftRouteKey();

		if (!routeKey) {
			logFastshiftTrace(`skip ${url} — current path is not a declared FastShift route`);

			return false;
		}

		if (!specificRoutes.includes(routeKey)) {
			logFastshiftTrace(
				`skip ${url} — route '${routeKey}' is not one of the configured routes`,
				specificRoutes,
			);

			return false;
		}

		logFastshiftTrace(`${url} — route '${routeKey}' is one of the configured routes`);
	}

	const roll = Math.random() * MAX_SAMPLE_RATE;
	const isSampled = roll < sampleRate;

	logFastshiftTrace(
		`${isSampled ? 'sample' : 'skip'} ${url} — rolled ${roll.toFixed(2)}, ${
			isSampled ? 'under' : 'not under'
		} the sample rate of ${sampleRate}`,
	);

	return isSampled;
};

/**
 * Wraps `window.fetch` so browser calls to the FastShift backend APIs on our own origins carry
 * `x-b3-sampled: '1'` at the rate and route scope configured in the `fastshift_portal_tracing`
 * dynamic config. The header is a head-sampling override: it forces a trace to be kept that the
 * receiving service's own sampling decision would otherwise drop.
 *
 * The API check applies whatever the configured route scope is — `allRoutes` widens which pages
 * are eligible, never which endpoints.
 *
 * The die is rolled per request, so a given page's requests land in independent traces.
 *
 * Install before the app renders. Safe to call more than once.
 */
export function installFastshiftTraceSampling(): void {
	if (isInstalled || typeof window === 'undefined' || typeof window.fetch !== 'function') {
		return;
	}

	isInstalled = true;

	const originalFetch = window.fetch;

	const sampledFetch: typeof window.fetch = (input, init) => {
		const url = getRequestUrl(input);

		if (!isTraceableOrigin(url)) {
			logFastshiftTrace(`skip ${url} — not one of our own origins`);

			return originalFetch(input, init);
		}

		if (!isFastshiftApiRequest(url)) {
			logFastshiftTrace(`skip ${url} — not a call to a FastShift backend API`);

			return originalFetch(input, init);
		}

		if (!shouldSampleRequest(url)) {
			return originalFetch(input, init);
		}

		// `init.headers` replaces a Request's own headers when both are given, so read from
		// whichever set will actually be sent.
		const sentHeaders =
			init?.headers ??
			(typeof input === 'object' && 'headers' in input ? input.headers : undefined);
		const headers = new Headers(sentHeaders);

		// Some callers hardcode the header; never override an explicit decision.
		if (headers.has(SAMPLED_HEADER)) {
			logFastshiftTrace(
				`skip ${url} — caller already set ${SAMPLED_HEADER}: '${headers.get(SAMPLED_HEADER)}'`,
			);

			return originalFetch(input, init);
		}

		headers.set(SAMPLED_HEADER, '1');

		logFastshiftTrace(`sampled ${url} — set ${SAMPLED_HEADER}: '1'`);

		return originalFetch(input, { ...init, headers });
	};

	Object.assign(window, { fetch: sampledFetch });

	logFastshiftTrace('installed the fetch interceptor');
}

/** Test-only: lets a spec reinstall against a fresh `fetch` stub. */
export function __resetFastshiftTraceSamplingForTests(): void {
	isInstalled = false;
}
